import { useNavigate } from "react-router-dom";
import { useForm } from "@tanstack/react-form";
import { toast } from "sonner";
import * as z from "zod";
import { useAuth } from "../../contexts/AuthContext";
import { useChangePassword } from "../../hooks/useChangePassword";
import { FormTextField } from "@/components/shared";
import { Button } from "@/components/ui/button";

const formSchema = z
  .object({
    currentPassword: z.string().min(1, "Senha atual é obrigatória"),
    password: z
      .string()
      .min(6, "A senha deve ter pelo menos 6 caracteres"),
    passwordConfirmation: z.string().min(1, "Confirmação de senha é obrigatória"),
  })
  .refine((data) => data.password === data.passwordConfirmation, {
    message: "As senhas não coincidem",
    path: ["passwordConfirmation"],
  });

export default function ForcePasswordChange() {
  const navigate = useNavigate();
  const { refetch } = useAuth();
  const { changePassword, loading } = useChangePassword({
    onCompleted: async () => {
      toast.success("Senha alterada com sucesso");
      await refetch();
      navigate("/dashboard");
    },
  });

  const form = useForm({
    defaultValues: {
      currentPassword: "",
      password: "",
      passwordConfirmation: "",
    },
    validators: {
      onSubmit: formSchema,
    },
    onSubmit: ({ value }) => {
      return changePassword(
        value.currentPassword,
        value.password,
        value.passwordConfirmation
      );
    },
  });

  return (
    <div className="min-h-screen flex items-center justify-center bg-primary-50 dark:bg-primary-950 px-4">
      <div className="w-full max-w-md">
        <div className="bg-white dark:bg-primary-900 border border-primary-200 dark:border-primary-800 p-8 sm:p-12">
          <div className="mb-8">
            <h1 className="text-2xl font-light tracking-tight text-primary-950 dark:text-white">
              Altere sua senha
            </h1>
            <p className="mt-2 text-sm text-primary-600 dark:text-primary-400">
              Por motivos de segurança, você deve alterar sua senha antes de continuar.
            </p>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              form.handleSubmit();
            }}
            className="space-y-6"
          >
            <FormTextField form={form} name="currentPassword" label="Senha Atual" type="password" />
            <FormTextField form={form} name="password" label="Nova Senha" type="password" />
            <FormTextField form={form} name="passwordConfirmation" label="Confirmar Nova Senha" type="password" />

            <form.Subscribe
              selector={(state) => [state.canSubmit, state.isSubmitting]}
            >
              {([canSubmit, isSubmitting]) => (
                <Button
                  type="submit"
                  disabled={!canSubmit || loading}
                  className="w-full rounded-none uppercase tracking-widest text-sm"
                >
                  {isSubmitting || loading ? "Atualizando..." : "Atualizar Senha"}
                </Button>
              )}
            </form.Subscribe>
          </form>
        </div>
      </div>
    </div>
  );
}
